import { useEffect, useMemo, useState } from "react";
import { formatDate } from "../lib/format";
import { TableSkeleton } from "../components/Loading";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  COMPLAINT_KINDS, complaintsApi,
  type Complaint, type ComplaintKind, type ComplaintStatus,
} from "../api/complaints";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { ComplaintKindTag, ComplaintStatusBadge, ComplaintUrgentTag } from "../components/ComplaintStatusBadge";
import { ComplaintDeadlineTag } from "../components/complaints/ComplaintDeadlineTag";
import { Select } from "../components/Select";
import Ic from "../components/icons.jsx";
import {
  distinctComplaintCompanies, filterComplaints, hasActiveComplaintFilter, outcomeTotalsByCurrency,
  EMPTY_COMPLAINT_FILTER, type ComplaintFilterCriteria,
} from "./complaintsFilter";

const STATUSES: ComplaintStatus[] = ["SUBMITTED", "IN_REVIEW", "RESOLVED", "REJECTED", "CLOSED"];

const STATUS_KEY: Record<ComplaintStatus, string> = {
  SUBMITTED: "submitted",
  IN_REVIEW: "inReview",
  RESOLVED: "resolved",
  REJECTED: "rejected",
  CLOSED: "closed",
};

/**
 * Reklamacije na kvalitet — one list for both sides of the portal (mirrors
 * OrdersPage's shape): the backend already scopes visibility per caller
 * (CUST_* own company, SALES/SALES_DIRECTOR assigned companies, KVALITET/
 * ADMIN/SUPERADMIN all), so the page loads the caller's full accessible list
 * once and layers a client-side status + customer filter on top — the
 * customer filter is internal-only (a CUST_* caller's list only ever
 * contains their own company, so distinctComplaintCompanies collapses to a
 * single entry and the dropdown hides itself).
 */
export default function ComplaintsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();

  const [items, setItems] = useState<Complaint[] | null>(null);
  const [criteria, setCriteria] = useState<ComplaintFilterCriteria>(EMPTY_COMPLAINT_FILTER);

  const canCreate = hasPermission("complaints:write");

  async function load() {
    try {
      setItems(await complaintsApi.list());
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  useEffect(() => { void load(); }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: 0 };
    if (!items) return c;
    c.ALL = items.length;
    for (const cpl of items) c[cpl.status] = (c[cpl.status] ?? 0) + 1;
    return c;
  }, [items]);

  const companies = useMemo(() => distinctComplaintCompanies(items ?? []), [items]);
  const filtered = useMemo(() => filterComplaints(items ?? [], criteria), [items, criteria]);

  // The settled value of what is on screen, grouped by currency. Amounts in different currencies are
  // never added together: without the rate on the day the sum would be a number that means nothing.
  const settled = useMemo(() => outcomeTotalsByCurrency(filtered), [filtered]);

  const clearAll = () => setCriteria(EMPTY_COMPLAINT_FILTER);

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <header style={pageHead}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>
            {t("complaints.list.title", "Complaints")}
          </h1>
          <div style={subText}>
            {items === null
              ? t("common.loading")
              : t("complaints.list.summary", "{{count}} complaints", { count: items.length })}
          </div>
        </div>
        {canCreate && (
          <Link to="/complaints/new" className="btn primary" style={{ textDecoration: "none" }}>
            <Ic.plus /> {t("complaints.list.newComplaint", "New complaint")}
          </Link>
        )}
      </header>

      <div style={chipBar}>
        <Chip
          label={`${t("complaints.filter.all", "All")} ${counts.ALL ?? 0}`}
          active={!criteria.status}
          onClick={() => setCriteria((c) => ({ ...c, status: "" }))}
        />
        {STATUSES.map((s) => {
          const n = counts[s] ?? 0;
          if (n === 0 && criteria.status !== s) return null;
          return (
            <Chip
              key={s}
              label={`${t(`complaints.status.${STATUS_KEY[s]}`, { defaultValue: s })} ${n}`}
              active={criteria.status === s}
              onClick={() => setCriteria((c) => ({ ...c, status: c.status === s ? "" : s }))}
            />
          );
        })}
      </div>

      <div style={filterBar}>
        {companies.length > 1 && (
          <Select<string>
            value={criteria.companyId}
            onChange={(v) => setCriteria((c) => ({ ...c, companyId: v }))}
            ariaLabel={t("complaints.filter.allCustomers", "All customers")}
            style={{ minWidth: 220 }}
            options={[
              { value: "", label: t("complaints.filter.allCustomers", "All customers") },
              ...companies.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
        )}
        <Select<string>
          value={criteria.kind}
          onChange={(v) => setCriteria((c) => ({ ...c, kind: v as ComplaintKind | "" }))}
          ariaLabel={t("complaints.filter.allKinds", "Sve vrste")}
          style={{ minWidth: 200 }}
          options={[
            { value: "", label: t("complaints.filter.allKinds", "Sve vrste") },
            ...COMPLAINT_KINDS.map((k) => ({ value: k, label: t(`complaints.kind.${k}`, { defaultValue: k }) })),
          ]}
        />
        <button
          type="button"
          className="btn sm"
          aria-pressed={criteria.urgentOnly}
          onClick={() => setCriteria((c) => ({ ...c, urgentOnly: !c.urgentOnly }))}
        >
          {t("complaints.filter.urgentOnly", "Samo hitne")}
        </button>
        {hasActiveComplaintFilter(criteria) && (
          <button type="button" className="btn sm" onClick={clearAll}>
            {t("complaints.filter.clear", "Clear filters")}
          </button>
        )}
      </div>

      {settled.length > 0 && (
        <div className="toolbar" aria-label={t("complaints.list.settledValue", "Upisana vrednost")}>
          <span className="muted">{t("complaints.list.settledValue", "Upisana vrednost")}:</span>
          {settled.map((s) => (
            <span key={s.currency} className="tag neutral">
              {s.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {s.currency}
              {" · "}
              {t("complaints.list.settledCount", "{{n}} rekl.", { n: s.count })}
            </span>
          ))}
        </div>
      )}

      {items === null ? (
        <TableSkeleton rows={6} cols={6} label={t("common.loading")} />
      ) : filtered.length === 0 ? (
        <div style={emptyCard}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>🧾</div>
          <b style={{ color: "var(--ink-2)" }}>{t("complaints.empty.title", "No complaints match.")}</b>
          <div style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4 }}>
            {items.length === 0
              ? t("complaints.empty.none", "No complaints have been filed yet.")
              : t("complaints.empty.filtered", "Try clearing the filters.")}
          </div>
          {items.length === 0 && canCreate ? (
            <Link to="/complaints/new" className="btn primary" style={{ textDecoration: "none", marginTop: 12 }}>
              <Ic.plus /> {t("complaints.list.newComplaint", "New complaint")}
            </Link>
          ) : items.length > 0 ? (
            <button type="button" className="btn sm" onClick={clearAll} style={{ marginTop: 12 }}>
              {t("complaints.filter.clear", "Clear filters")}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="card" style={{ overflow: "hidden", padding: 0 }}>
          <table style={tbl} className="list-card">
            <thead>
              <tr>
                <th style={th}>{t("complaints.table.caseNumber", "No.")}</th>
                <th style={th}>{t("complaints.table.title", "Title")}</th>
                {companies.length > 1 && <th style={th}>{t("complaints.table.customer", "Customer")}</th>}
                <th style={th}>{t("complaints.table.order", "Order")}</th>
                <th style={th}>{t("complaints.table.lot", "LOT")}</th>
                <th style={th}>{t("complaints.table.kind", "Vrsta")}</th>
                <th style={th}>{t("complaints.table.status", "Status")}</th>
                <th style={th}>{t("complaints.table.finalDeadline", "Konačan rok")}</th>
                <th style={th}>{t("complaints.table.createdAt", "Filed")}</th>
                <th style={th} aria-label={t("complaints.table.open", "Open")} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/complaints/${c.id}`)} style={{ cursor: "pointer" }}>
                  <td style={td} data-label={t("complaints.table.caseNumber", "No.")}>
                    <code style={mono}>{c.caseNumber}</code>
                  </td>
                  <td style={td} data-label={t("complaints.table.title", "Title")}>
                    <b style={{ color: "var(--ink)" }}>{c.title}</b>
                  </td>
                  {companies.length > 1 && (
                    <td style={td} data-label={t("complaints.table.customer", "Customer")}>
                      {c.companyName}
                    </td>
                  )}
                  <td style={td} data-label={t("complaints.table.order", "Order")}>
                    {c.orderNumber ? <code style={mono}>{c.orderNumber}</code> : <span style={{ color: "var(--ink-4)" }}>—</span>}
                  </td>
                  <td style={td} data-label={t("complaints.table.lot", "LOT")}>
                    {c.lot ?? <span style={{ color: "var(--ink-4)" }}>—</span>}
                  </td>
                  <td style={td} data-label={t("complaints.table.kind", "Vrsta")}>
                    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
                      <ComplaintKindTag kind={c.kind} />
                      <ComplaintUrgentTag urgent={c.urgent} />
                      {!c.kind && !c.urgent && <span style={{ color: "var(--ink-4)" }}>—</span>}
                    </span>
                  </td>
                  <td style={td} data-label={t("complaints.table.status", "Status")}>
                    <ComplaintStatusBadge status={c.status} />
                  </td>
                  <td style={td} data-label={t("complaints.table.finalDeadline", "Konačan rok")}>
                    <ComplaintDeadlineTag dueAt={c.dueFinalAt} answeredAt={c.finalResponseAt} />
                  </td>
                  <td style={{ ...td, color: "var(--ink-3)", fontSize: 12.5 }} data-label={t("complaints.table.createdAt", "Filed")}>
                    {formatDate(c.createdAt)}
                  </td>
                  <td style={{ ...td, textAlign: "right" }} className="card-actions">
                    <Link to={`/complaints/${c.id}`} className="btn sm" style={{ textDecoration: "none" }} onClick={(e) => e.stopPropagation()}>
                      {t("complaints.row.open", "Open")} <Ic.arrowRight />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? "var(--brand-primary)" : "var(--surface)",
        color: active ? "var(--on-primary)" : "var(--ink-2)",
        border: `1px solid ${active ? "var(--brand-primary)" : "var(--border)"}`,
        padding: "6px 12px",
        borderRadius: 999,
        fontSize: 12.5,
        cursor: "pointer",
        fontWeight: 500,
        transition: "all .12s ease",
      }}
    >
      {label}
    </button>
  );
}

const pageHead: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" };
const subText: React.CSSProperties = { fontSize: 13, color: "var(--ink-3)", marginTop: 4 };
const chipBar: React.CSSProperties = { display: "flex", gap: 8, flexWrap: "wrap" };
const filterBar: React.CSSProperties = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };
const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: React.CSSProperties = { textAlign: "left", padding: "11px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "12px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5, color: "var(--ink-2)" };
const emptyCard: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 32, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" };
